// `window.fetch` interceptor for the native Create Claim module's PP-API
// endpoints. Runs in any build (including deployed dev/demo) when opted in
// via the per-env ENV_FILE's VITE_MOCK_PP_API=true — see main.tsx's gate.
//
// AM-5619 "Part 1" AND "Part 2" are both DEPLOYED on providerPortalAPI dev, so
// nearly every Create Claim endpoint is real. As of 2026-07-30 exactly ONE has
// no server-side implementation on any branch, and it is all this file stubs:
//   - SearchCPTCodeByTypes (aka SearchCptByType)
// Every other Create Claim PP-API request (SaveClaim, UploadFiles,
// GetProviderNPI, GetOtherProviderNPI, GetRmiProvKeyIdFromRendNpi,
// GetInvalidPrimDxInitials, the CreateClaim shell seed, VerifyNPI,
// SearchClaimServiceCodes, GetVendors, AuthMember/POS,
// AuthMember/ModifierCodes, AuthMember/GetFacilities) is REAL and falls
// through to the actual dev API untouched. Diagnosis search is also real: it is
// NOT a claim-specific endpoint — it's the SHARED `Utils/SearchDiagCodes` that
// the live CreateAuthV2 form already calls, so it is never stubbed here.
//
// ⚠️ NEVER add a route here for a DEPLOYED endpoint. A mock that shadows a live
// service hides real contract drift behind fixtures that always agree with the
// client, which is worse than no mock at all. Two routes were removed on
// 2026-07-30 for exactly this reason (SearchClaimServiceCodes and GetVendors had
// both shipped to dev while this file still intercepted them), and a third —
// the entry gate's GetClaimReadWriteProviderList — was removed on principle: it
// is the Create Claim AUTHORIZATION gate, and a fixture that answers "yes" has
// no business running in any environment, least of all a deployed one. While
// that endpoint is undeployed the gate fails closed and Create Claim is
// correctly unavailable; that is the intended interim state, not a gap to fake.
//
// This installs a monkeypatched `window.fetch` that recognizes the Create
// Claim PP-API path BY SUBSTRING (not the exact base URL — see below) and
// answers with realistic fixture JSON for just that one endpoint so the
// guided form doesn't 404 on the piece still missing server-side. Every
// other request (Apollo GraphQL, MSAL/B2C token calls, any other host
// fetch, and all the now-real Create Claim endpoints) falls through to the
// real `fetch` untouched.
//
// WHY SUBSTRING, NOT EXACT BASE URL: locally, VITE_PROVIDER_PORTAL_API_BASE_URL
// is unset, so MemberProfilePage.tsx's `` `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api` ``
// evaluates to the literal string "/api" (an empty-string env var, not JS
// `undefined` — Vite still substitutes empty string). RTK Query's
// `fetchBaseQuery({ baseUrl: "/api" })` then joins that with each endpoint's
// relative url, producing a same-origin relative fetch like
// "/api/ClaimMember/SaveClaim" — never a real host. Matching on the known
// PATH SEGMENTS (e.g. "/ClaimMember/SearchClaimServiceCodes") is what makes
// this robust to that "/api" prefix (or any other base a deployed env might
// inject) without hardcoding the base.
//
// FIXTURES: adapted from the module repo's own Storybook fixture/fetch-mock
// (astrana-ui-modules-create-claim/.../CreateClaim/CreateClaim.mock.ts +
// CreateClaim.stories.tsx), which the module's own stories already rely on to
// render the form fully populated without a live backend. Reproduced here
// (not imported) so this file has zero dependency on the sibling module
// worktree's path existing in every environment this host builds in.
//
// DEP-FREE: no MSW, no new packages — a hand-rolled Response() factory over
// the ambient fetch, mirroring the module's own approach.
//
// GATING: this file only EXPORTS the installer. The env-flag gate lives at
// the call site (main.tsx), per task boundary — this module makes no
// decision about whether it should run.

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

// ============================================================================
// Fixtures — same shapes as the module's DTOs (store/types.ts), values
// adapted from the module's own Storybook demo fixture (CreateClaim.mock.ts).
// ============================================================================

/** GET SearchCPTCodeByTypes?userId&types — E&M / CatII code lists, keyed off
 *  the `types` query param (mirrors the module's own Storybook mock). */
const MOCK_EM_CODES = [
  { Code: "99213", Description: "Office/outpatient visit, established patient, low complexity" },
  { Code: "99214", Description: "Office/outpatient visit, established patient, moderate complexity" },
];
const MOCK_MEASURE_CODES = [
  { Code: "3074F", Description: "Blood pressure measured and recorded" },
];

// ============================================================================
// Route table. Every `test` receives the pathname (already stripped of
// origin/query) and the uppercased HTTP method.
//
// Verified 2026-07-30 against `providerPortalAPI origin/dev`: AM-5619 "Part 1"
// and "Part 2" are both deployed, and `SearchClaimServiceCodes`
// (ClaimMemberController.cs:813) and `GetVendors` (:196) have since shipped too.
// `SearchCPTCodeByTypes` is the ONLY claim endpoint the FE consumes that still
// has zero server-side implementation on any branch, so it is the only route
// left here.
//
// Verification method (do NOT regress to route-string grep): confirm an
// endpoint exists by METHOD NAME across all *Controller.cs on the target ref,
// e.g. `git grep -n -P '(public|internal)[^(]*\bNAME\b\s*\(' origin/dev -- '*Controller.cs'`
// — `git grep -E` supports neither `\b` nor `\s` and silently matches nothing,
// and convention-routed controllers carry no literal route string at all. ALWAYS
// run a known-BUILT action through the detector first: a stale "unbuilt" belief
// is how two live endpoints ended up shadowed by fixtures here, and how a false
// "claims cannot be submitted" blocker reached a QA ticket.
//
// Diagnosis search is NOT stubbed here — it's the SHARED `Utils/SearchDiagCodes`
// endpoint (real, deployed) that the live CreateAuthV2 form also calls.
// ============================================================================

interface MockRoute {
  /** Short label used only in the console.info log line. */
  label: string;
  test: (method: string, pathname: string) => boolean;
  handle: (searchParams: URLSearchParams) => Response;
}

// ONLY endpoints that exist on NO providerPortalAPI branch belong here. A mock
// for a DEPLOYED endpoint is actively harmful: it shadows the real service and
// hides real contract drift behind fixtures that always agree with the client.
//
// Removed 2026-07-30 after verifying against `providerPortalAPI origin/dev`:
//   - ClaimMember/SearchClaimServiceCodes  -> BUILT (ClaimMemberController.cs:813).
//     Backs the service-line CPT typeahead. Its request model is
//     EnquireServiceSearchModel — the EnquireService contract.
//   - ClaimMember/GetVendors               -> BUILT (ClaimMemberController.cs:196).
//   - ClaimMember/GetClaimReadWriteProviderList -> shipping alongside this change;
//     use the REAL call. It is also the Create Claim AUTHORIZATION gate, and a
//     stub that answers "yes" is the wrong thing to have in any environment.
//     Until it deploys the gate fails closed and Create Claim is correctly
//     unavailable — that is the intended interim state, not something to fake.
const MOCK_ROUTES: MockRoute[] = [
  {
    // Real wire path is "SearchCPTCodeByTypes" (createClaimApi.ts's
    // searchCptByType endpoint); also matches the shorter "SearchCptByType"
    // spelling defensively in case a future route rename lands.
    //
    // The last genuinely-unbuilt claim endpoint (exists on no branch). NOTE it
    // only feeds the E&M duplicate-check + CatII measure lists, which the module
    // deliberately degrades to a VISIBLE WARNING rather than blocking submission
    // (the AM-5783 carve-out). Mocking it therefore HIDES from developers the
    // warning every real user sees — consider dropping this route (and this whole
    // file) so local dev exercises the real degradation path.
    label: "GET SearchCPTCodeByTypes",
    test: (m, p) => m === "GET" && (p.includes("/SearchCPTCodeByTypes") || p.includes("/SearchCptByType")),
    handle: (searchParams) => {
      const types = searchParams.get("types") ?? "";
      return jsonResponse(types.includes("CatII") ? MOCK_MEASURE_CODES : MOCK_EM_CODES);
    },
  },
];

// ============================================================================
// Installer
// ============================================================================

/**
 * ISOLATION FLAG. The host sets `window.__ppClaimsDemoActive = true` for exactly
 * as long as the native Create CLAIM form is mounted (see MemberProfilePage —
 * derived synchronously from `createClaimOpen` so it's set before the form's
 * first lookup fires). The interceptor below is fully INERT unless this is true,
 * so the mock ONLY ever serves the Claims demo surface. Create Auth — which
 * shares the `AuthMember/*` lookup URLs the mock can't tell apart by path — is
 * never touched, and neither is any other request in the app.
 *
 * The Create Claim ENTRY GATE is deliberately OUTSIDE this window. Its
 * provider-list endpoint is not mocked at all (see the header): it is the
 * authorization gate, so it must always talk to the real service or fail closed.
 */
interface ClaimsDemoWindow {
  __ppClaimsDemoActive?: boolean;
}
function isClaimsDemoActive(): boolean {
  return (window as ClaimsDemoWindow).__ppClaimsDemoActive === true;
}

let installed = false;

/**
 * Monkeypatches `window.fetch` to answer the native Create Claim module's
 * PP-API calls with fixture JSON instead of hitting the (not-yet-deployed)
 * real backend. Idempotent — safe to call more than once (e.g. under Vite
 * HMR). Interception is scoped to the Claims demo form via
 * `isClaimsDemoActive()` (see the ISOLATION FLAG above); when neither is open the
 * patched fetch is a transparent pass-through to the real backend.
 */
export function installPpApiMock(): void {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    // ISOLATION GATE: unless the Claims demo form is open, intercept NOTHING —
    // every request (the entry gate's provider list, Create Auth, GraphQL,
    // MSAL, …) goes straight to real fetch.
    if (!isClaimsDemoActive()) {
      return originalFetch(input, init);
    }

    const rawUrl =
      typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();

    let pathname = rawUrl;
    let searchParams = new URLSearchParams();
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      pathname = parsed.pathname;
      searchParams = parsed.searchParams;
    } catch {
      /* fall through with rawUrl as pathname — substring matching still works */
    }

    const route = MOCK_ROUTES.find((r) => r.test(method, pathname));
    if (route) {
      // eslint-disable-next-line no-console
      console.info(`[mockPpApi] intercepted ${method} ${rawUrl} -> ${route.label}`);
      return route.handle(searchParams);
    }

    return originalFetch(input, init);
  }) as typeof window.fetch;

  // eslint-disable-next-line no-console
  console.info(
    "[mockPpApi] Create Claim PP-API fetch mock installed — ACTIVE ONLY while the " +
      "Claims form is open (window.__ppClaimsDemoActive). Only SearchCPTCodeByTypes, " +
      "the one endpoint still unbuilt on providerPortalAPI, resolves to fixture data; " +
      "every other Create Claim call (including the entry gate's provider list, " +
      "SearchClaimServiceCodes and GetVendors), Create Auth, and all other requests " +
      "pass through to the real backend.",
  );
}
